import { useState, useMemo, useEffect, Fragment } from "react";
import { useAccounting } from "@/contexts/AccountingContext";
import { useAuth } from "@/contexts/AuthContext";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { Voucher, VoucherType, VoucherStatus } from "@/lib/accounting/types";
import { GSTInvoiceForm } from "@/components/accounting/GSTInvoiceForm";
import { InvoicePreviewDialog } from "@/components/accounting/InvoicePreviewDialog";
import { UPIQRPreview, RecordUPIPaymentDialog } from "@/components/accounting/UPIPaymentSection";
import { WhatsAppQuickButton } from "@/components/accounting/WhatsAppInvoiceSender";
import { ReminderQuickButton, ReminderDashboard } from "@/components/accounting/WhatsAppPaymentReminders";
import { PaymentLinkButton } from "@/components/accounting/PaymentGateway";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, FileText, Download, Printer, Eye, CheckCircle2, XCircle,
  Clock, Trash2, ChevronDown, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight, Mail, Plus, Filter, QrCode, Smartphone, IndianRupee, BellRing, Link2, Truck
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { renderInvoiceHTML } from "@/components/accounting/InvoiceTemplates";
import { useNavigate } from "react-router-dom";

const statusConfig: Record<VoucherStatus, { icon: typeof CheckCircle2; className: string }> = {
  Approved: { icon: CheckCircle2, className: "text-success bg-success/10" },
  Pending: { icon: Clock, className: "text-warning bg-warning/10" },
  Rejected: { icon: XCircle, className: "text-destructive bg-destructive/10" },
  Draft: { icon: Clock, className: "text-muted-foreground bg-muted" },
  Cancelled: { icon: Trash2, className: "text-muted-foreground bg-muted/50" },
};

const INVOICE_TABS: { type: VoucherType; label: string; icon: typeof FileText }[] = [
  { type: "Sales", label: "Sales Invoices", icon: FileText },
  { type: "Purchase", label: "Purchase Invoices", icon: FileText },
  { type: "Credit Note", label: "Credit Notes", icon: FileText },
  { type: "Debit Note", label: "Debit Notes", icon: FileText },
];

const InvoicesPage = () => {
  const { vouchers, approveVoucher, rejectVoucher, deleteVoucher } = useAccounting();
  const { role } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Sales");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<VoucherStatus | "All">("All");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [previewVoucher, setPreviewVoucher] = useState<Voucher | null>(null);
  const [upiPaymentVoucher, setUpiPaymentVoucher] = useState<Voucher | null>(null);
  const [showReminders, setShowReminders] = useState(false);
  const [reminderKey, setReminderKey] = useState(0);

  // High-performance pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const filtered = useMemo(() => {
    return vouchers
      .filter((v) => v.type === activeTab)
      .filter((v) => statusFilter === "All" || v.status === statusFilter)
      .filter((v) =>
        v.voucherNumber.toLowerCase().includes(search.toLowerCase()) ||
        v.partyName?.toLowerCase().includes(search.toLowerCase()) ||
        v.narration.toLowerCase().includes(search.toLowerCase()) ||
        v.invoiceNumber?.toLowerCase().includes(search.toLowerCase())
      );
  }, [vouchers, activeTab, search, statusFilter]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const paginatedInvoices = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [search, activeTab, statusFilter, pageSize]);

  const stats = useMemo(() => {
    const typeVouchers = vouchers.filter((v) => v.type === activeTab);
    return {
      total: typeVouchers.length,
      approved: typeVouchers.filter((v) => v.status === "Approved").length,
      pending: typeVouchers.filter((v) => v.status === "Pending").length,
      totalValue: typeVouchers.filter((v) => v.status !== "Cancelled").reduce((s, v) => s + v.totalDebit, 0),
    };
  }, [vouchers, activeTab]);

  const perms = getAccountingPermissions(role, activeTab as VoucherType);

  const handleQuickPrint = (v: Voucher) => {
    const html = renderInvoiceHTML(v, "classic");
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); setTimeout(() => win.print(), 500); }
  };

  const handleQuickDownload = (v: Voucher) => {
    const html = renderInvoiceHTML(v, "classic");
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${v.invoiceNumber || v.voucherNumber}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: `Invoice ${v.invoiceNumber || v.voucherNumber} saved` });
  };

  const handleExportAll = () => {
    const rows = [
      ["Invoice No", "Voucher No", "Date", "Party", "Amount", "GST", "Status"].join(","),
      ...filtered.map((v) =>
        [v.invoiceNumber || "", v.voucherNumber, v.date, v.partyName || "", v.totalDebit, v.gstAmount || 0, v.status].join(",")
      ),
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}-invoices.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${filtered.length} invoices exported` });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Invoice Management</h1>
          <p className="text-sm text-muted-foreground">Create, preview, print, and manage all invoices and notes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowReminders(!showReminders)} className="gap-1.5">
            <BellRing className="h-3.5 w-3.5" /> Reminders
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportAll} className="gap-1.5">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
          <GSTInvoiceForm type={activeTab as VoucherType} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Invoices", value: stats.total, icon: FileText, color: "text-accent" },
          { label: "Approved", value: stats.approved, icon: CheckCircle2, color: "text-success" },
          { label: "Pending", value: stats.pending, icon: Clock, color: "text-warning" },
          { label: "Total Value", value: `₹${stats.totalValue.toLocaleString("en-IN")}`, icon: FileText, color: "text-foreground" },
        ].map((s) => (
          <div key={s.label} className="bg-card border border-border rounded-lg p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <s.icon className={`h-4 w-4 ${s.color}`} />
              <span className="text-xs text-muted-foreground">{s.label}</span>
            </div>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Reminder Dashboard */}
      {showReminders && (
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <BellRing className="h-4 w-4 text-warning" /> WhatsApp Payment Reminders
            </h3>
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowReminders(false)}>Close</Button>
          </div>
          <ReminderDashboard key={reminderKey} vouchers={vouchers} />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/50">
          {INVOICE_TABS.map((tab) => (
            <TabsTrigger key={tab.type} value={tab.type} className="text-sm gap-1.5">
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {INVOICE_TABS.map((tab) => (
          <TabsContent key={tab.type} value={tab.type} className="space-y-4 mt-4">
            {/* Search & Filter */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-card border border-border rounded-md px-3 py-2 flex-1 max-w-sm">
                <Search className="h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search invoices..."
                  className="bg-transparent text-sm outline-none w-full text-foreground placeholder:text-muted-foreground"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" />
                {(["All", "Approved", "Pending", "Rejected", "Cancelled"] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${statusFilter === s ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <span className="text-xs text-muted-foreground ml-auto">{filtered.length} results</span>
            </div>

            {/* Table */}
            <div className="rounded-lg bg-card border border-border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="w-8 py-3 px-4"></th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Invoice</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Party</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Amount</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">GST</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">E-Invoice</th>
                      <th className="text-left py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                      <th className="text-right py-3 px-4 font-medium text-muted-foreground text-xs uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvoices.map((v) => {
                      const sc = statusConfig[v.status];
                      const isExpanded = expandedId === v.id;
                      return (
                        <Fragment key={v.id}>
                          <tr
                            key={v.id}
                            className="border-t border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                            onClick={() => setExpandedId(isExpanded ? null : v.id)}
                          >
                            <td className="py-3 px-4">
                              {isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-medium text-[#2563EB] dark:text-blue-400 hover:text-blue-700 transition-colors">{v.invoiceNumber || v.voucherNumber}</span>
                              {v.invoiceNumber && <span className="text-xs text-[#2563EB]/80 dark:text-blue-400/80 block hover:text-blue-700 transition-colors">{v.voucherNumber}</span>}
                            </td>
                            <td className="py-3 px-4 text-foreground">{new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                            <td className="py-3 px-4 text-foreground">{v.partyName || "—"}</td>
                            <td className="py-3 px-4 text-right font-semibold text-foreground">₹{v.totalDebit.toLocaleString("en-IN")}</td>
                            <td className="py-3 px-4 text-right text-muted-foreground">{v.gstAmount ? `₹${v.gstAmount.toLocaleString("en-IN")}` : "—"}</td>
                            <td className="py-3 px-4">
                              {v.irn ? (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate("/dashboard/e-invoice"); }}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-green-500/10 text-green-700 hover:bg-green-500/20 border border-green-200 transition-colors"
                                  title={`IRN: ${v.irn}`}
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  IRN Active
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => { e.stopPropagation(); navigate("/dashboard/e-invoice"); }}
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                                  title="Generate Offline IRN"
                                >
                                  <QrCode className="h-3 w-3" />
                                  Generate IRN
                                </button>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${sc.className}`}>
                                <sc.icon className="h-3 w-3" />
                                {v.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Preview" onClick={() => setPreviewVoucher(v)}>
                                  <Eye className="h-3.5 w-3.5 text-muted-foreground" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Print" onClick={() => handleQuickPrint(v)}>
                                  <Printer className="h-3.5 w-3.5 text-muted-foreground" />
                                </Button>
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Download" onClick={() => handleQuickDownload(v)}>
                                                  <Download className="h-3.5 w-3.5 text-muted-foreground" />
                                                </Button>
                                <WhatsAppQuickButton voucher={v} />
                                {(v.type === "Sales" || v.type === "Debit Note") && v.status === "Approved" && (
                                  <PaymentLinkButton voucher={v} />
                                )}
                                {(v.type === "Sales" || v.type === "Debit Note") && v.status === "Approved" && (
                                  <ReminderQuickButton voucher={v} onScheduleCreated={() => setReminderKey((k) => k + 1)} />
                                )}
                                {v.status === "Pending" && perms.canApprove && (
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { approveVoucher(v.id); toast({ title: "Approved" }); }}>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                                  </Button>
                                )}
                                {v.status !== "Cancelled" && perms.canDelete && (
                                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { deleteVoucher(v.id); toast({ title: "Cancelled" }); }}>
                                    <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${v.id}-detail`} className="bg-muted/20">
                              <td colSpan={9} className="px-4 py-4">
                                <div className="space-y-3">
                                  <div className="text-xs text-muted-foreground">
                                    <strong>Narration:</strong> {v.narration}
                                    {v.approvedBy && <> &nbsp;|&nbsp; <strong>Approved by:</strong> {v.approvedBy}</>}
                                  </div>
                                  {(v.irn || v.ewayBillNo) && (
                                    <div className="flex flex-wrap gap-4 p-3 bg-card border border-border rounded-md text-xs">
                                      {v.irn && (
                                        <div className="space-y-0.5">
                                          <span className="font-bold text-primary flex items-center gap-1">
                                            <QrCode className="h-3.5 w-3.5" /> Govt IRN (E-Invoice):
                                          </span>
                                          <code className="font-mono text-[11px] text-foreground bg-muted px-1.5 py-0.5 rounded block max-w-lg truncate">{v.irn}</code>
                                          {v.ackNo && <span className="text-[10px] text-muted-foreground block">Ack No: {v.ackNo} | {v.ackDate ? new Date(v.ackDate).toLocaleDateString("en-IN") : ""}</span>}
                                        </div>
                                      )}
                                      {v.ewayBillNo && (
                                        <div className="space-y-0.5">
                                          <span className="font-bold text-primary flex items-center gap-1">
                                            <Truck className="h-3.5 w-3.5" /> E-Way Bill Number:
                                          </span>
                                          <code className="font-mono text-xs font-bold text-foreground bg-muted px-1.5 py-0.5 rounded block">{v.ewayBillNo}</code>
                                          {v.ewayBillValidUntil && <span className="text-[10px] text-muted-foreground block">Valid Until: {new Date(v.ewayBillValidUntil).toLocaleDateString("en-IN")}</span>}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {v.items && v.items.length > 0 && (
                                    <div className="space-y-1">
                                      <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider">Invoice Items</span>
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="border-b border-border bg-muted/40">
                                            <th className="text-left py-1 px-2 font-medium text-muted-foreground">Item Description</th>
                                            <th className="text-center py-1 px-2 font-medium text-muted-foreground">HSN</th>
                                            <th className="text-center py-1 px-2 font-medium text-muted-foreground">Qty</th>
                                            <th className="text-right py-1 px-2 font-medium text-muted-foreground">Rate</th>
                                            <th className="text-right py-1 px-2 font-medium text-muted-foreground">Amount</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {v.items.map((it, idx) => (
                                            <tr key={idx} className="border-b border-border/30">
                                              <td className="py-1 px-2 text-foreground font-medium">{it.name || it.description}</td>
                                              <td className="py-1 px-2 text-center text-muted-foreground">{it.hsnCode || "—"}</td>
                                              <td className="py-1 px-2 text-center">{it.qty} {it.unit || "Pcs"}</td>
                                              <td className="py-1 px-2 text-right">₹{Number(it.rate).toLocaleString("en-IN")}</td>
                                              <td className="py-1 px-2 text-right font-medium">₹{Number(it.amount).toLocaleString("en-IN")}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  <div className="space-y-1">
                                    <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Ledger Accounting Entries</span>
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="border-b border-border">
                                          <th className="text-left py-1.5 font-medium text-muted-foreground">Ledger</th>
                                          <th className="text-right py-1.5 font-medium text-muted-foreground">Debit (₹)</th>
                                          <th className="text-right py-1.5 font-medium text-muted-foreground">Credit (₹)</th>
                                        </tr>
                                      </thead>
                                    <tbody>
                                      {v.lines.map((line) => (
                                        <tr key={line.id} className="border-b border-border/30">
                                          <td className="py-1.5 text-foreground">{line.ledgerName}</td>
                                          <td className="py-1.5 text-right font-medium">{line.debit > 0 ? `₹${line.debit.toLocaleString("en-IN")}` : "—"}</td>
                                          <td className="py-1.5 text-right font-medium">{line.credit > 0 ? `₹${line.credit.toLocaleString("en-IN")}` : "—"}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                  {v.gstAmount && v.gstAmount > 0 && (
                                    <div className="text-xs text-muted-foreground">
                                      GST: ₹{v.gstAmount.toLocaleString("en-IN")}
                                      {v.cgst ? ` (CGST: ₹${v.cgst.toLocaleString("en-IN")})` : ""}
                                      {v.sgst ? ` (SGST: ₹${v.sgst.toLocaleString("en-IN")})` : ""}
                                      {v.igst ? ` (IGST: ₹${v.igst.toLocaleString("en-IN")})` : ""}
                                    </div>
                                  )}
                                  <div className="flex flex-wrap gap-2 pt-2">
                                    <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={() => setPreviewVoucher(v)}>
                                      <Eye className="h-3 w-3" /> Preview with Templates
                                    </Button>
                                    <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={() => handleQuickPrint(v)}>
                                      <Printer className="h-3 w-3" /> Print
                                    </Button>
                                    <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={() => handleQuickDownload(v)}>
                                      <Download className="h-3 w-3" /> Download
                                    </Button>
                                    {(v.type === "Sales" || v.type === "Debit Note") && v.status === "Approved" && (
                                      <Button size="sm" className="gap-1.5 text-xs h-7 bg-success text-white hover:bg-success/90" onClick={() => setUpiPaymentVoucher(v)}>
                                        <Smartphone className="h-3 w-3" /> Record UPI Payment
                                      </Button>
                                    )}
                                  </div>
                                  {(v.type === "Sales" || v.type === "Debit Note") && v.status === "Approved" && (
                                    <div className="mt-3">
                                      <UPIQRPreview voucher={v} />
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={9} className="py-16 text-center">
                          <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                          <p className="text-muted-foreground font-medium">No {tab.label.toLowerCase()} found</p>
                          <p className="text-xs text-muted-foreground mt-1">Create a new invoice to get started</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {filtered.length > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 bg-card border border-border rounded-lg text-xs shadow-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <span>
                    Showing <strong className="text-foreground">{(page - 1) * pageSize + 1}</strong> to{" "}
                    <strong className="text-foreground">{Math.min(page * pageSize, filtered.length)}</strong> of{" "}
                    <strong className="text-foreground">{filtered.length}</strong> invoices
                  </span>
                  <span>•</span>
                  <div className="flex items-center gap-1.5">
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="bg-muted border border-border rounded px-2 py-0.5 text-xs text-foreground outline-none cursor-pointer"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={page <= 1}
                    onClick={() => setPage(1)}
                    title="First Page"
                  >
                    <ChevronsLeft className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-3.5 w-3.5" /> Prev
                  </Button>

                  <span className="px-2 font-medium text-foreground">
                    Page {page} of {totalPages}
                  </span>

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2 text-xs gap-1"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={page >= totalPages}
                    onClick={() => setPage(totalPages)}
                    title="Last Page"
                  >
                    <ChevronsRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Preview Dialog */}
      {previewVoucher && (
        <InvoicePreviewDialog
          voucher={previewVoucher}
          open={!!previewVoucher}
          onOpenChange={(open) => !open && setPreviewVoucher(null)}
        />
      )}

      {/* UPI Payment Dialog */}
      {upiPaymentVoucher && (
        <RecordUPIPaymentDialog
          voucher={upiPaymentVoucher}
          open={!!upiPaymentVoucher}
          onOpenChange={(open) => !open && setUpiPaymentVoucher(null)}
        />
      )}
    </div>
  );
};

export default InvoicesPage;
